import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AudioLines, Play, Pause, ScanFace, Quote, Search, Check, UserRound, SkipForward, UserX, ArrowLeft, BookOpen, Calendar, MapPin, Users, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import HubLayout from '../common/HubLayout';
import { getJobSpeakers, confirmSpeakers, completeJob, searchPeople, resolveSpeaker } from '../../services/videoActivationAPI';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";

const AVATAR_COLORS = ['#556B2F', '#A63C00', '#5B3F7A', '#9A4122', '#2F5A8A', '#6E531A', '#4E6B1C', '#9A3A5B'];
const avatarColor = (name) => AVATAR_COLORS[name ? name.charCodeAt(0) % AVATAR_COLORS.length : 0];

const fmtTime = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
};
// "speaker_A" -> "Voice A"
const voiceName = (label) => `Voice ${(label || '').replace(/^speaker[_\s-]*/i, '').toUpperCase() || '?'}`;
const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  return isNaN(d) ? null : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

export default function SpeakerConfirmation() {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [videos, setVideos] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [videoIdx, setVideoIdx] = useState(0);
  const [speakerIdx, setSpeakerIdx] = useState(0);
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const [selectedName, setSelectedName] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [guestMode, setGuestMode] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [currentTime, setCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [showTranscript, setShowTranscript] = useState(false);
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!jobId) return;
    setLoading(true);
    getJobSpeakers(jobId)
      .then(data => {
        const vids = (data.videos || [])
          .map(v => ({
            ...v,
            speakers: (v.speakers || []).filter(s => s.resolution === 'unresolved' || s.resolution === 'deferred'),
          }))
          .filter(v => v.speakers.length > 0);
        setVideos(vids);
        setFamilyMembers(data.family_members || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [jobId]);

  // Typeahead search
  useEffect(() => {
    if (!memberSearch.trim()) { setSearchResults(null); return; }
    const timer = setTimeout(() => {
      searchPeople(memberSearch, 20)
        .then(data => setSearchResults(data.people || []))
        .catch(() => setSearchResults([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [memberSearch]);

  const currentVideo = videos[videoIdx];
  const speaker = currentVideo?.speakers[speakerIdx];
  const totalVideos = videos.length;
  const totalSpeakers = videos.reduce((s, v) => s + v.speakers.length, 0);
  const currentSpeakerGlobal = videos.slice(0, videoIdx).reduce((s, v) => s + v.speakers.length, 0) + speakerIdx;
  const progressPct = totalSpeakers > 0 ? Math.round((currentSpeakerGlobal / totalSpeakers) * 100) : 0;

  const candidates = searchResults || familyMembers;

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const videoUrl = currentVideo?.media_file_id ? `${API_BASE}/api/media/files/${currentVideo.media_file_id}/serve/` : null;

  const togglePlay = useCallback(() => {
    // Prefer video element if available
    const el = videoRef.current || audioRef.current;
    if (!el) return;
    if (isPlaying) { el.pause(); setIsPlaying(false); }
    else { el.play().catch(() => {}); setIsPlaying(true); }
  }, [isPlaying]);

  const seekTo = useCallback((ms, autoplay = true) => {
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = (ms || 0) / 1000;
    if (autoplay) { el.play().catch(() => {}); setIsPlaying(true); }
  }, []);

  // When the voice changes, jump the video to where that voice first speaks
  useEffect(() => {
    if (speaker?.first_start_ms != null) seekTo(speaker.first_start_ms, false);
  }, [speaker?.speaker_label, currentVideo?.record_id]); // eslint-disable-line

  const advanceToNext = () => {
    setSelectedPersonId(null);
    setSelectedName('');
    setMemberSearch('');
    setSearchResults(null);
    setIsPlaying(false);
    setGuestMode(false);
    setGuestName('');
    const isLastSpeaker = speakerIdx >= (currentVideo?.speakers.length || 0) - 1;
    const isLastVideo = videoIdx >= totalVideos - 1;
    if (isLastSpeaker && isLastVideo) {
      // Only complete if server says fully confirmed
      completeJob(jobId)
        .then(() => navigate(-1))
        .catch(e => {
          console.error('Cannot complete — unresolved speakers remain:', e);
          // Reload to show remaining speakers
          getJobSpeakers(jobId).then(data => {
            const vids = (data.videos || [])
              .map(v => ({ ...v, speakers: (v.speakers || []).filter(s => s.resolution === 'unresolved' || s.resolution === 'deferred') }))
              .filter(v => v.speakers.length > 0);
            setVideos(vids);
            setVideoIdx(0);
            setSpeakerIdx(0);
          });
        });
    } else if (isLastSpeaker) {
      setVideoIdx(i => i + 1);
      setSpeakerIdx(0);
    } else {
      setSpeakerIdx(i => i + 1);
    }
  };

  const handleConfirm = useCallback(async () => {
    if (!selectedPersonId || !speaker || !currentVideo) return;
    setSaving(true);
    try {
      await resolveSpeaker(jobId, currentVideo.record_id, speaker.speaker_label, 'confirmed', selectedPersonId);
      advanceToNext();
    } catch (e) { console.error('Confirm failed:', e); }
    finally { setSaving(false); }
  }, [selectedPersonId, speaker, currentVideo, jobId]); // eslint-disable-line

  const handleDismiss = useCallback(async () => {
    if (!speaker || !currentVideo) return;
    setSaving(true);
    try {
      await resolveSpeaker(jobId, currentVideo.record_id, speaker.speaker_label, 'dismissed');
      advanceToNext();
    } catch (e) { console.error('Dismiss failed:', e); }
    finally { setSaving(false); }
  }, [speaker, currentVideo, jobId]); // eslint-disable-line

  const handleGuest = useCallback(async () => {
    if (!speaker || !currentVideo || !guestName.trim()) return;
    setSaving(true);
    try {
      await resolveSpeaker(jobId, currentVideo.record_id, speaker.speaker_label, 'guest', null, guestName.trim());
      advanceToNext();
    } catch (e) { console.error('Guest failed:', e); }
    finally { setSaving(false); }
  }, [speaker, currentVideo, jobId, guestName]); // eslint-disable-line

  const handleDefer = useCallback(async () => {
    if (!speaker || !currentVideo) return;
    setSaving(true);
    try {
      await resolveSpeaker(jobId, currentVideo.record_id, speaker.speaker_label, 'deferred');
      advanceToNext();
    } catch (e) { console.error('Defer failed:', e); }
    finally { setSaving(false); }
  }, [speaker, currentVideo, jobId]); // eslint-disable-line

  if (loading) {
    return (
      <HubLayout>
        <div style={{ padding: '80px 24px', textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, border: '4px solid #556B2F', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ fontSize: 18, color: '#556B2F' }}>Loading voices...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </HubLayout>
    );
  }

  if (!speaker) {
    return (
      <HubLayout>
        <div style={{ padding: '80px 24px', textAlign: 'center' }}>
          <p style={{ fontSize: 22, fontWeight: 700, color: '#556B2F', marginBottom: 8, fontFamily: FONT_H }}>All done!</p>
          <p style={{ fontSize: 16, color: '#6B8E23' }}>No more voices to identify.</p>
          <button onClick={() => navigate('/board')} style={{ marginTop: 24, background: '#556B2F', color: 'white', border: 'none', borderRadius: 12, padding: '14px 32px', fontSize: 18, fontWeight: 700, cursor: 'pointer' }}>
            Back to the family board
          </button>
        </div>
      </HubLayout>
    );
  }

  const segEnd = Math.round((speaker.total_duration_ms || 0) / 1000);
  const totalSecs = videoDuration || currentVideo.duration_seconds || 0;
  const playedPct = totalSecs > 0 ? Math.min(100, (currentTime / totalSecs) * 100) : 0;
  const story = currentVideo.story;
  const storyDate = fmtDate(story?.date) || fmtDate(currentVideo.uploaded_at);
  const videoTitle = story?.title || currentVideo.title;
  const transcript = currentVideo.transcript || [];
  const activeSegIdx = transcript.findIndex(t => currentTime * 1000 >= t.start_ms && currentTime * 1000 < t.end_ms);

  return (
    <HubLayout>
      <audio ref={audioRef} onEnded={() => setIsPlaying(false)} style={{ display: 'none' }} />

      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '24px 28px 60px' }}>
        <button onClick={() => navigate(-1)} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
          color: '#3E4F22', fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: FONT_B,
          padding: '10px 4px', minHeight: 44, marginBottom: 8,
        }}>
          <ArrowLeft size={18} /> Back
        </button>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#E65100', animation: 'pulse 1.5s infinite' }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: '#3E4F22', textTransform: 'uppercase', letterSpacing: '.5px', fontFamily: FONT_B }}>Identify voices</span>
            </div>
            <h1 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 34, color: '#2C2F26', margin: '0 0 6px' }}>Who is speaking?</h1>
            <p style={{ fontSize: 17, color: '#3F4436', fontFamily: FONT_B, maxWidth: 500, lineHeight: 1.5 }}>
              Watch the clip, then tell us whose voice this is — once matched, this becomes part of their wisdom.
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#2C2F26', fontFamily: FONT_B, marginBottom: 6 }}>
              Voice {currentSpeakerGlobal + 1} of {totalSpeakers} · {progressPct}%
            </div>
            <div style={{ width: 200, height: 10, background: '#C9C6B4', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 999, background: 'linear-gradient(90deg, #6B8E23, #556B2F)', width: `${progressPct}%`, transition: 'width .3s' }} />
            </div>
          </div>
        </div>

        {/* Two-column layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 32, alignItems: 'start' }} className="iv-grid">
          {/* LEFT — Media */}
          <div>
            {/* About this video — context for recognising the voices */}
            <div style={{ background: '#fff', border: '1px solid #E6E4D8', borderRadius: 16, padding: '16px 20px', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <BookOpen size={16} color="#3E4F22" />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#3E4F22', textTransform: 'uppercase', letterSpacing: '.5px', fontFamily: FONT_B }}>
                  {story ? 'From this story' : 'About this video'}
                </span>
              </div>
              <h2 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 21, color: '#2C2F26', margin: '0 0 8px' }}>
                {videoTitle || 'Family video'}
              </h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', fontSize: 15, color: '#3F4436', fontFamily: FONT_B }}>
                {(story?.author || currentVideo.uploaded_by) && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <UserRound size={15} /> Shared by <strong>{story?.author || currentVideo.uploaded_by}</strong>
                  </span>
                )}
                {storyDate && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Calendar size={15} /> {storyDate}</span>
                )}
                {story?.location && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><MapPin size={15} /> {story.location}</span>
                )}
                {story?.people?.length > 0 && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Users size={15} /> About {story.people.join(', ')}</span>
                )}
              </div>
              {story?.excerpt && (
                <p style={{ fontSize: 15.5, color: '#3F4436', fontFamily: FONT_B, lineHeight: 1.55, margin: '10px 0 0' }}>
                  {story.excerpt}{story.excerpt.length >= 400 ? '…' : ''}
                </p>
              )}
              {story?.id && (
                <a href={`/stories/${story.id}`} target="_blank" rel="noreferrer" style={{
                  display: 'inline-block', marginTop: 10, color: '#3E4F22', fontWeight: 700, fontSize: 15,
                  fontFamily: FONT_B, textDecoration: 'underline', textUnderlineOffset: 3,
                }}>
                  Open the full story
                </a>
              )}
            </div>

            {/* Video player */}
            <div style={{
              background: '#1C2018', borderRadius: 18, overflow: 'hidden', position: 'relative',
              aspectRatio: '16/9', boxShadow: '0 14px 40px rgba(28,32,24,.28)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12,
            }}>
              {/* Speaking now chip */}
              <div style={{
                position: 'absolute', top: 14, left: 14, display: 'flex', alignItems: 'center', gap: 6,
                background: 'rgba(28,32,22,.6)', backdropFilter: 'blur(4px)', borderRadius: 999,
                padding: '5px 12px', color: '#9BBE4E', fontSize: 12, fontWeight: 700, fontFamily: FONT_B,
              }}>
                <AudioLines size={14} /> Speaking now
              </div>

              {/* Video element */}
              {videoUrl ? (
                <video
                  ref={videoRef}
                  src={videoUrl}
                  onEnded={() => setIsPlaying(false)}
                  onTimeUpdate={e => setCurrentTime(e.currentTarget.currentTime)}
                  onLoadedMetadata={e => setVideoDuration(e.currentTarget.duration || 0)}
                  onClick={togglePlay}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'absolute', inset: 0 }}
                  preload="metadata"
                />
              ) : (
                <UserRound size={60} color="rgba(255,255,255,.15)" />
              )}

              {/* Play/pause button overlay */}
              {!isPlaying && (
                <button onClick={togglePlay} style={{
                  position: 'absolute', width: 76, height: 76, borderRadius: '50%',
                  background: '#556B2F', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background .2s',
                  zIndex: 2,
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#6B8E23'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#556B2F'; }}
                >
                  <Play size={28} color="#fff" style={{ marginLeft: 4 }} />
                </button>
              )}

              {/* Caption */}
              <div style={{ position: 'absolute', bottom: 14, left: 14, fontSize: 13, color: 'rgba(255,255,255,.6)', fontFamily: FONT_B }}>
                Family video
              </div>
            </div>

            {/* Scrubber */}
            <div style={{
              background: '#232A1D', borderRadius: 10, padding: '10px 16px', marginBottom: 16,
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <span style={{ fontSize: 13, color: '#9BBE4E', fontFamily: 'ui-monospace, monospace', fontVariantNumeric: 'tabular-nums' }}>{fmtTime(currentTime)}</span>
              <div
                onClick={e => {
                  if (!totalSecs) return;
                  const r = e.currentTarget.getBoundingClientRect();
                  seekTo(((e.clientX - r.left) / r.width) * totalSecs * 1000, false);
                }}
                style={{ flex: 1, height: 10, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              >
                <div style={{ width: '100%', height: 4, background: 'rgba(255,255,255,.15)', borderRadius: 999, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', left: 0, width: `${playedPct}%`, height: '100%', background: '#E65100', borderRadius: 999 }} />
                </div>
              </div>
              <span style={{ fontSize: 13, color: 'rgba(255,255,255,.5)', fontFamily: 'ui-monospace, monospace' }}>
                {fmtTime(totalSecs)}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#9BBE4E', fontFamily: FONT_B }}>
                <ScanFace size={12} /> {speaker.segment_count} segment{speaker.segment_count !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Transcript card */}
            <div style={{ background: '#fff', border: '1px solid #E6E4D8', borderRadius: 16, padding: '18px 22px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Quote size={16} color="#7A4E05" />
                <span style={{ fontWeight: 700, fontSize: 14, color: '#2C2F26', fontFamily: FONT_B }}>What was said</span>
                <span style={{ fontSize: 14, color: '#4A4F40', fontFamily: FONT_B }}>
                  · {speaker.segment_count} segment{speaker.segment_count !== 1 ? 's' : ''} · {segEnd}s
                </span>
              </div>
              <p style={{ fontFamily: FONT_H, fontStyle: 'italic', fontWeight: 500, fontSize: 18, lineHeight: 1.55, color: '#2C2F26', margin: 0 }}>
                "{speaker.sample_text}"
              </p>
              <button onClick={() => seekTo(speaker.first_start_ms)} style={{
                marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none',
                border: '2px solid #3E4F22', borderRadius: 10, padding: '8px 14px', minHeight: 44,
                color: '#3E4F22', fontWeight: 700, fontSize: 15, fontFamily: FONT_B, cursor: 'pointer',
              }}>
                <Play size={15} /> Play this voice
              </button>
            </div>

            {/* Full transcript (optional, collapsed by default) */}
            {transcript.length > 0 && (
              <div style={{ background: '#fff', border: '1px solid #E6E4D8', borderRadius: 16, marginTop: 14, overflow: 'hidden' }}>
                <button onClick={() => setShowTranscript(s => !s)} aria-expanded={showTranscript} style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none',
                  padding: '16px 22px', cursor: 'pointer', minHeight: 56, textAlign: 'left',
                }}>
                  <FileText size={16} color="#3E4F22" />
                  <span style={{ fontWeight: 700, fontSize: 15, color: '#2C2F26', fontFamily: FONT_B, flex: 1 }}>
                    Full transcript · {transcript.length} lines
                  </span>
                  {showTranscript ? <ChevronUp size={18} color="#3E4F22" /> : <ChevronDown size={18} color="#3E4F22" />}
                </button>
                {showTranscript && (
                  <div style={{ padding: '0 22px 18px', maxHeight: 360, overflowY: 'auto' }}>
                    {transcript.map((t, i) => {
                      const mine = t.speaker_label === speaker.speaker_label;
                      return (
                        <div key={i} onClick={() => seekTo(t.start_ms)} style={{
                          display: 'flex', gap: 10, padding: '8px 10px', borderRadius: 10, cursor: 'pointer', marginBottom: 2,
                          background: i === activeSegIdx ? '#FFF1E0' : mine ? '#F1F5E8' : 'transparent',
                        }}>
                          <span style={{ fontSize: 12.5, color: '#4A4F40', fontFamily: 'ui-monospace, monospace', paddingTop: 3, flexShrink: 0 }}>{fmtTime(t.start_ms / 1000)}</span>
                          <div style={{ fontFamily: FONT_B, fontSize: 15.5, lineHeight: 1.5, color: '#2C2F26' }}>
                            <strong style={{ color: mine ? '#3E4F22' : '#4A4F40' }}>{voiceName(t.speaker_label)}{mine ? ' (this voice)' : ''}: </strong>
                            {t.text}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT — Match panel */}
          <div style={{ position: 'sticky', top: 24 }}>
            <div style={{ background: '#fff', border: '1px solid #E6E4D8', borderRadius: 18, padding: '22px 24px', boxShadow: '0 6px 24px rgba(54,60,40,.08)' }}>
              <h3 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 20, color: '#2C2F26', margin: '0 0 4px' }}>Match this voice</h3>
              <p style={{ fontSize: 15.5, color: '#3F4436', fontFamily: FONT_B, margin: '0 0 14px' }}>
                <strong style={{ color: '#4A4F40' }}>{familyMembers.length} in family</strong> · Pick the family member you hear.
              </p>

              {/* Search */}
              <div style={{ position: 'relative', marginBottom: 14 }}>
                <Search size={16} color="#5A6150" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  value={memberSearch}
                  onChange={e => setMemberSearch(e.target.value)}
                  placeholder="Search family members..."
                  style={{
                    width: '100%', padding: '11px 14px 11px 38px', borderRadius: 10,
                    border: '1.5px solid #9A9F8A', fontSize: 16, fontFamily: FONT_B, outline: 'none',
                    minHeight: 48, boxSizing: 'border-box',
                  }}
                  onFocus={e => { e.target.style.borderColor = '#6B8E23'; e.target.style.boxShadow = '0 0 0 3px rgba(107,142,35,.12)'; }}
                  onBlur={e => { e.target.style.borderColor = '#E4E1D2'; e.target.style.boxShadow = 'none'; }}
                />
              </div>

              {/* Candidate list */}
              <div style={{ maxHeight: 340, overflowY: 'auto', marginBottom: 16 }}>
                {candidates.length === 0 && (
                  <div style={{ padding: '20px 0', textAlign: 'center', fontSize: 14, color: '#9AA088', fontFamily: FONT_B }}>No matches</div>
                )}
                {candidates.map(member => {
                  const pid = member.person_id || member.id;
                  const name = member.first_name ? `${member.first_name} ${member.last_name || ''}`.trim() : member.name;
                  const initial = (member.first_name || member.name || '?').charAt(0).toUpperCase();
                  const isSelected = selectedPersonId === pid;
                  return (
                    <div
                      key={pid}
                      onClick={() => { setSelectedPersonId(pid); setSelectedName(member.first_name || name?.split(' ')[0]); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                        borderRadius: 12, cursor: 'pointer', transition: 'background .1s',
                        border: isSelected ? '2px solid #3E4F22' : '2px solid #D6D1BD',
                        background: isSelected ? '#EAF1DC' : '#fff',
                        marginBottom: 4, minHeight: 60,
                      }}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#FAFAF3'; }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{
                        width: 42, height: 42, borderRadius: '50%', background: avatarColor(name),
                        color: '#fff', fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>{initial}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 16, color: '#2C2F26', fontFamily: FONT_B }}>{name}</div>
                        {member.relationship && <div style={{ fontSize: 14, color: '#4A4F40', fontFamily: FONT_B }}>{member.relationship}</div>}
                      </div>
                      {member.is_minor && (
                        <span style={{ background: '#FBEFD8', color: '#9A6A0A', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999 }}>child</span>
                      )}
                      <div style={{
                        width: 26, height: 26, borderRadius: '50%', border: isSelected ? 'none' : '2px solid #7F846F',
                        background: isSelected ? '#556B2F' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        {isSelected && <Check size={13} color="#fff" />}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Confirm button */}
              <button
                onClick={handleConfirm}
                disabled={!selectedPersonId || saving}
                style={{
                  width: '100%', minHeight: 56, borderRadius: 13,
                  border: selectedPersonId && !saving ? 'none' : '2px dashed #9A9F8A',
                  fontSize: 17, fontWeight: 700,
                  cursor: selectedPersonId && !saving ? 'pointer' : 'default',
                  background: selectedPersonId && !saving ? '#3E4F22' : '#EFEDE3',
                  color: selectedPersonId && !saving ? '#fff' : '#4A4F40',
                  fontFamily: FONT_B, transition: 'all .2s', marginBottom: 12,
                }}
              >
                {saving ? 'Saving...' : selectedPersonId ? `Confirm ${selectedName}` : 'Select who is speaking'}
              </button>

              {/* Secondary actions — real buttons, not text links */}
              {!guestMode ? (
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
                  gap: 10, marginBottom: 12,
                }}>
                  <button onClick={handleDefer} disabled={saving}
                    style={{
                      minHeight: 64, borderRadius: 13, border: '2px solid #3E4F22',
                      background: '#fff', cursor: saving ? 'not-allowed' : 'pointer',
                      padding: '10px 14px', textAlign: 'left', transition: 'background .1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F1F5E8'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <SkipForward size={18} color="#3E4F22" style={{ flexShrink: 0 }} />
                      <span style={{ fontSize: 16.5, fontWeight: 700, color: '#3E4F22', fontFamily: FONT_B, whiteSpace: 'nowrap' }}>Skip for now</span>
                    </div>
                    <div style={{ fontSize: 13.5, color: '#4A4F40', fontFamily: FONT_B, paddingLeft: 26 }}>We'll ask again later</div>
                  </button>
                  <button onClick={() => setGuestMode(true)} disabled={saving}
                    style={{
                      minHeight: 64, borderRadius: 13, border: '2px solid #9A9F8A',
                      background: '#fff', cursor: saving ? 'not-allowed' : 'pointer',
                      padding: '10px 14px', textAlign: 'left', transition: 'background .1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F6F5EE'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <UserX size={18} color="#2C2F26" style={{ flexShrink: 0 }} />
                      <span style={{ fontSize: 16.5, fontWeight: 700, color: '#2C2F26', fontFamily: FONT_B, whiteSpace: 'nowrap' }}>Not a family member</span>
                    </div>
                    <div style={{ fontSize: 13.5, color: '#4A4F40', fontFamily: FONT_B, paddingLeft: 26 }}>Guest or stranger</div>
                  </button>
                </div>
              ) : (
                <div style={{
                  background: '#F7F6EE', border: '2px solid #9A9F8A', borderRadius: 13,
                  padding: '14px 16px', marginBottom: 12,
                }}>
                  <p style={{ fontSize: 15, color: '#3F4436', margin: '0 0 10px', fontFamily: FONT_B }}>
                    What's their name? They'll be labeled as a guest speaker.
                  </p>
                  <input
                    value={guestName}
                    onChange={e => setGuestName(e.target.value)}
                    placeholder="e.g. Coach Williams, Pastor Mike"
                    autoFocus
                    style={{
                      width: '100%', padding: '12px 14px', border: '1.5px solid #9A9F8A',
                      borderRadius: 10, fontSize: 16, fontFamily: FONT_B, color: '#2C2F26',
                      outline: 'none', marginBottom: 10, boxSizing: 'border-box', minHeight: 48,
                    }}
                    onKeyDown={e => { if (e.key === 'Enter' && guestName.trim()) handleGuest(); }}
                  />
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={handleGuest} disabled={!guestName.trim() || saving}
                      style={{
                        flex: 1, padding: '12px', borderRadius: 10, border: 'none',
                        background: guestName.trim() && !saving ? '#3E4F22' : '#EFEDE3',
                        color: guestName.trim() && !saving ? '#fff' : '#4A4F40',
                        fontWeight: 700, fontSize: 15, cursor: guestName.trim() && !saving ? 'pointer' : 'default',
                        fontFamily: FONT_B, minHeight: 48,
                      }}>
                      Save as Guest
                    </button>
                    <button onClick={handleDismiss} disabled={saving}
                      style={{
                        padding: '12px 16px', borderRadius: 10, border: '2px solid #9A9F8A',
                        background: '#fff', color: '#2C2F26', fontSize: 15, cursor: saving ? 'not-allowed' : 'pointer',
                        fontFamily: FONT_B, fontWeight: 600, minHeight: 48,
                      }}>
                      Skip entirely
                    </button>
                    <button onClick={() => { setGuestMode(false); setGuestName(''); }}
                      style={{
                        padding: '12px 16px', borderRadius: 10, border: 'none',
                        background: 'none', color: '#4A4F40', fontSize: 15, cursor: 'pointer',
                        fontFamily: FONT_B, fontWeight: 600,
                      }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Save progress link */}
              <div style={{ textAlign: 'center', marginTop: 10 }}>
                <button onClick={() => navigate(-1)} style={{
                  background: 'none', border: 'none', color: '#3E4F22', fontSize: 15,
                  fontWeight: 600, cursor: 'pointer', fontFamily: FONT_B,
                  textDecoration: 'underline', textUnderlineOffset: '3px',
                  padding: '10px 4px', minHeight: 44,
                }}>
                  Save progress and come back later
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
        @media (max-width: 900px) { .iv-grid { grid-template-columns: 1fr !important; } }
        @media (max-width: 640px) { .iv-grid { gap: 16px !important; padding: 16px 12px 40px !important; } }
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; animation: none !important; } }
      `}</style>
    </HubLayout>
  );
}
